import React, { useEffect, useState } from 'react';
import { Input, Modal, TextArea, useForm } from 'linkwithus';
import './styles.scss';
import { motion } from 'framer-motion';
import config from 'config';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import SiteButton from 'components/SiteButton';
import FileUploader from 'components/FileUploader';
import { EventModalMode, IEventForm, IEventModalProps } from './interface';
import { contentSettingsModalMotion } from './animation';

function EventModal({
    modalVisibility,
    close,
    item,
    mode = EventModalMode.Edit,
    updateList,
}: IEventModalProps) {
    const { t } = useTranslation();
    const [imageUrl, setImageUrl] = useState<string>();
    const [imageVisibility, setImageVisibility] = useState(false);

    const { register, form, submit, updateForm, setForm } = useForm<IEventForm>(
        {
            url: `/events/${
                mode === EventModalMode.Edit && item ? item.Id : ''
            }`,
            type: mode === EventModalMode.Edit ? 'PUT' : 'POST',
            onSuccess: () => {
                notification.success({
                    message: t('general.SUCCESS'),
                    description: t(
                        `contentSettingsPage.${
                            mode === EventModalMode.Edit
                                ? 'EVENT_EDITED_WITH_SUCCESS'
                                : 'EVENT_CREATED_WITH_SUCCESS'
                        }`,
                    ),
                });
                updateList();
                close();
            },
            onError: (data, error: any) => {
                const errorCode = Number(error.errorCode) || 0;
                notification.error({
                    message: t(`general.ERROR`),
                    description: t(`errors.${errorCode}`),
                });
                updateList();
                close();
            },
            resolveData(rawForm) {
                const data = new FormData();
                Object.keys(rawForm).forEach(key => {
                    if (key === 'photo') {
                        rawForm.photo.forEach((f: any) => {
                            data.append('photo', f as any);
                        });
                    } else
                        data.append(
                            key,
                            rawForm[key as keyof IEventForm] as any,
                        );
                });
                return data;
            },
        },
    );

    useEffect(() => {
        if (modalVisibility && mode === EventModalMode.Edit && !item) {
            close();
        }
        if (modalVisibility && mode === EventModalMode.Edit && item) {
            const currentDate = new Date();
            updateForm('name', item.Eventlanguages[0].Name);
            updateForm('description', item.Eventlanguages[0].Description);
            setImageUrl(
                `${config.API_URL}/events/images/${item.Id}?date=${currentDate}`,
            );
            setTimeout(() => {
                setImageVisibility(true);
            }, 100);
        }
        if (!modalVisibility) {
            setForm({});
            setImageVisibility(false);
        }
        if (modalVisibility && mode === EventModalMode.Create) {
            setImageUrl(undefined);
            setTimeout(() => {
                setImageVisibility(true);
            }, 100);
        }
    }, [modalVisibility]);

    return (
        <Modal
            visible={modalVisibility}
            close={close}
            className='event-modal'
            width={400}
        >
            <motion.div
                {...contentSettingsModalMotion}
                className='event-modal__content'
            >
                <p className='event-modal__content__title'>
                    {t(
                        `contentSettingsPage.${
                            mode === EventModalMode.Edit
                                ? 'EDIT_EVENT'
                                : 'CREATE_EVENT'
                        }`,
                    )}
                </p>
                <div className='event-modal__content__body'>
                    <p className='event-modal__content__body__label'>
                        {t('contentSettingsPage.EVENT_NAME')}
                    </p>
                    <Input
                        {...register('name', {
                            required: true,
                            resolveError: () =>
                                `${t('form.NAME')} ${t('form.REQUIRED_FIELD')}`,
                        })}
                        className='event-modal__content__body__field'
                    />
                    <p className='event-modal__content__body__label'>
                        {t('form.DESCRIPTION')}
                    </p>
                    <TextArea
                        {...register('description', {
                            required: true,
                            resolveError: () =>
                                `${t('form.DESCRIPTION')} ${t(
                                    'form.REQUIRED',
                                )}`,
                        })}
                        autoSize
                        className='event-modal__content__body__field'
                    />
                    {imageVisibility && (
                        <FileUploader
                            {...register('photo')}
                            initial={imageUrl}
                            className='rectangular'
                        />
                    )}
                </div>
                <div className='event-modal__content__footer'>
                    <SiteButton
                        text={t('general.CANCEL')}
                        type='primary'
                        className='event-modal__content__footer__button grey'
                        onClick={close}
                    />
                    <SiteButton
                        text={t('form.SAVE_CHANGES')}
                        type='primary'
                        className='event-modal__content__footer__button'
                        onClick={submit}
                        disabled={
                            mode === EventModalMode.Create &&
                            (!form.name ||
                                !form.description ||
                                !form.photo ||
                                (form.photo &&
                                    Array.isArray(form.photo) &&
                                    !(form.photo.length > 0)))
                        }
                    />
                </div>
            </motion.div>
        </Modal>
    );
}

export default EventModal;
